import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMaterialManager } from "../hooks/useMaterialManager";
import { useAuth } from "../hooks/useAuth";
import { useNotifications } from "../hooks/useNotifications";
import HamburgerMenu from "../Components/HamburgerMenu/HamburgerMenu";
import HamburgerButton from "../Components/HamburgerMenu/HamburgerButton";
import { ModalMessage } from "../Components/ModalMessage/ModalMessage";
import MaterialCard from "../Components/MaterialCard/MaterialCard";
import Footer from "../Components/Footer/Footer";
import "./VitrinePage.css";

interface Material {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  thumbnailKey: string;
  thumbnailUrl?: string;
  previewUrl: string;
  purchaseUrl: string;
  referenceId?: string;
  templateId?: string;
  contentIds: string[];
  contentCount?: number;
  createdAt?: string;
  isFree?: boolean;
}

const VitrinePage: React.FC = () => {
  const { materialState, fetchMaterials } = useMaterialManager();
  const { hasRecentNotifications, fetchNotifications } = useNotifications();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const { isAuthenticated, logout: authLogout } = useAuth();
  const navigate = useNavigate();

  // Buscar materiais e notificações quando o componente carrega
  useEffect(() => {
    fetchMaterials();
    fetchNotifications();
  }, [fetchMaterials, fetchNotifications]);

  const filteredMaterials = (
    Array.isArray(materialState.materials) ? materialState.materials : []
  ).filter((material) => {
    const matchesSearch =
      material.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleMaterialClick = (material: Material) => {
    navigate(`/material/${material.id}`);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    authLogout();
    setShowLogoutModal(false);
  };

  if (materialState.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando materiais...</p>
      </div>
    );
  }

  if (materialState.error) {
    return (
      <div className="error-container">
        <h2>Ops! Algo deu errado</h2>
        <p>{materialState.error}</p>
        <button onClick={fetchMaterials} className="retry-button">
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="vitrine-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-left">
            {isAuthenticated && (
              <HamburgerButton 
                isOpen={isMenuOpen} 
                onClick={toggleMenu}
                hasNotification={hasRecentNotifications()}
              />
            )}
          </div>
          <div className="header-actions">
            <button
              onClick={
                isAuthenticated ? handleLogout : () => navigate("/login")
              }
              className="auth-button"
            >
              {isAuthenticated ? "Sair" : "Entrar"}
            </button>
          </div>
        </div>
      </header>

      {isAuthenticated && (
        <HamburgerMenu 
          isOpen={isMenuOpen} 
          onClose={closeMenu}
          hasRecentNotifications={hasRecentNotifications()}
        />
      )}

      <main className="page-main">
        <div className="page-title">
          <h1>Produtos Disponíveis</h1>
        </div>

        <div className="filters-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Buscar materiais..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="materials-grid">
          {filteredMaterials.length > 0 ? (
            filteredMaterials.map((material) => (
              <MaterialCard
                key={material.id}
                id={material.id}
                title={material.title}
                description={material.description}
                thumbnailUrl={material.thumbnailUrl}
                isFree={material.isFree}
                onClick={() => handleMaterialClick(material)}
              />
            ))
          ) : (
            <div className="no-results">
              <h3>Nenhum material encontrado</h3>
              <p>Tente ajustar os filtros ou termo de busca</p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {showLogoutModal && (
        <ModalMessage
          title="Tem certeza?"
          description="Deseja realmente sair da sua conta?"
          leftButtonText="Sim"
          leftButtonAction={confirmLogout}
          rightButtonText="Não"
          rightButtonAction={() => setShowLogoutModal(false)}
        />
      )}
    </div>
  );
};

export default VitrinePage;
